/**
 * Unit conversion utilities for the Cairn application
 * Handles conversions between metric and imperial units
 */

export type UnitSystem = 'metric' | 'imperial'

/**
 * Elevation unit conversions
 */
export function convertElevation(meters: number, targetSystem: UnitSystem): string {
  if (targetSystem === 'imperial') {
    const feet = meters * 3.28084
    return `${Math.round(feet)} ft`
  }
  return `${Math.round(meters)} m`
}

/**
 * Velocity unit conversions
 */
export function convertVelocity(metersPerSecond: number, targetSystem: UnitSystem): string {
  if (targetSystem === 'imperial') {
    const mph = metersPerSecond * 2.23694
    return `${Math.round(mph)} mph`
  }

  const kmh = metersPerSecond * 3.6
  return `${Math.round(kmh)} km/h`
}

/**
 * Distance unit conversions
 */
export function convertDistance(meters: number, targetSystem: UnitSystem): string {
  if (targetSystem === 'imperial') {
    if (meters < 1609.34) {
      // Less than 1 mile
      const feet = meters * 3.28084
      return `${Math.round(feet)} ft`
    } else {
      const miles = meters / 1609.34
      return `${miles.toFixed(1)} mi`
    }
  }

  if (meters < 1000) {
    return `${Math.round(meters)} m`
  } else {
    const km = meters / 1000
    return `${km.toFixed(1)} km`
  }
}

/**
 * Temperature unit conversions
 */
export function convertTemperature(celsius: number, targetSystem: UnitSystem): string {
  if (targetSystem === 'imperial') {
    const fahrenheit = (celsius * 9) / 5 + 32
    return `${Math.round(fahrenheit)}°F`
  }
  return `${Math.round(celsius)}°C`
}

/**
 * Speed unit conversions for GPS tracking
 */
export function convertSpeed(metersPerSecond: number, targetSystem: UnitSystem): string {
  return convertVelocity(metersPerSecond, targetSystem)
}

export function convertArea(squareMeters: number, targetSystem: UnitSystem): string {
  if (targetSystem === 'imperial') {
    const sqFeet = squareMeters * 10.7639
    if (sqFeet < 43560) {
      return `${Math.round(sqFeet)} ft²`
    }
    const acres = squareMeters / 4046.86
    if (acres < 640) {
      return `${acres.toFixed(2)} acres`
    }
    const sqMiles = squareMeters / 2589988.11
    return `${sqMiles.toFixed(2)} mi²`
  }

  if (squareMeters < 10000) {
    return `${Math.round(squareMeters)} m²`
  }
  const hectares = squareMeters / 10000
  if (hectares < 100) {
    return `${hectares.toFixed(2)} ha`
  }
  const sqKm = squareMeters / 1000000
  return `${sqKm.toFixed(2)} km²`
}

export function formatBearing(degrees: number): string {
  const normalized = ((degrees % 360) + 360) % 360
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  const index = Math.round(normalized / 45) % 8
  return `${Math.round(normalized)}° ${dirs[index]}`
}
